import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon } from "@fortawesome/free-solid-svg-icons";
import logo from '../assets/logo.png';

const DARK = {
  mode: "dark",
  accent: "#0C69C8",
  accentBgSubtle: "rgba(12,105,200,0.08)",
  accentBorder: "rgba(12,105,200,0.20)",
  bg: "#0A0A0A",
  bgCard: "#141414",
  bgElevated: "#1E1E1E",
  border: "rgba(255,255,255,0.08)",
  borderMedium: "rgba(255,255,255,0.12)",
  shadowMd: "0 4px 16px rgba(0,0,0,0.4)",
  text: "#FAFAFA",
  textSecondary: "rgba(255,255,255,0.70)",
  textMuted: "rgba(255,255,255,0.40)",
  headerBg: "rgba(10,10,10,0.92)",
  scrollbarThumb: "rgba(255,255,255,0.12)",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
};

const LIGHT = {
  mode: "light",
  accent: "#0C69C8",
  accentBgSubtle: "rgba(12,105,200,0.05)",
  accentBorder: "rgba(12,105,200,0.15)",
  bg: "#F5F7FA",
  bgCard: "#FFFFFF",
  bgElevated: "#FFFFFF",
  border: "rgba(10,29,68,0.08)",
  borderMedium: "rgba(10,29,68,0.12)",
  shadowMd: "0 4px 16px rgba(10,29,68,0.08)",
  text: "#0F1629",
  textSecondary: "#3D4A6B",
  textMuted: "#7A87A8",
  headerBg: "rgba(245,247,250,0.95)",
  scrollbarThumb: "rgba(10,29,68,0.12)",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
};

const Terms = () => {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("spa-theme") !== "light"; } catch { return true; }
  });

  const C = isDark ? DARK : LIGHT;

  const toggleTheme = useCallback(() => {
    setIsDark((v) => {
      const next = !v;
      try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);

  const sections = [
    {
      num: "01", title: "Acceptance of Terms",
      body: "By downloading, installing, accessing, or using Snows ProAm, you agree to be bound by these Terms of Use. If you do not agree to these Terms, do not use the App or website.",
    },
    {
      num: "02", title: "Description of Service",
      body: <>
        Snows ProAm provides a sports, fitness, booking, social, coaching, challenge, tournament, and payments platform. Features may include venue discovery and booking, workout tools, activity tracking, nutrition tracking, messaging, leaderboards, ProAm Coins, Stripe payments, subscriptions, and other related services. Nutrition information may be supplied by fatsecret. By using features that display fatsecret content, you also agree to the fatsecret Platform API <a href="https://platform.fatsecret.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: C.accent, fontWeight: 600 }}>Terms of Use</a>. Product information can change, so use the nutrition label on the package when it differs from the app.
      </>,
    },
    {
      num: "03", title: "Eligibility",
      body: "You must be at least 12 years old to use the App. In Ireland, if you are under 16 and we rely on consent to process your personal data in order to provide an online service directly to you, a parent or guardian must authorise that consent. Student users should ask a parent or guardian to review these Terms and our privacy information before creating an account.",
    },
    {
      num: "04", title: "Student Accounts and School Challenges",
      body: <>Student accounts receive additional privacy and safety protections. Live location sharing is disabled and the profile remains in Ghost Mode; Highlights cannot be viewed or uploaded; public Communities and global chats are unavailable; adding friends is disabled; and student leaderboard views are limited to student accounts. School or challenge administrators may see information needed to run the programme, such as a student's account identity, school or year-group membership, step totals, progress, and rank. Location may still be processed privately when a student deliberately starts a route-tracking feature and grants device permission, but it is not shared as a live map location. Schools remain responsible for their own notices, lawful basis, approvals, and data-protection obligations. See our <a href="/student-privacy" style={{ color: C.accent, fontWeight: 600 }}>Student Privacy &amp; Safety Notice</a> for a plain-language summary for students, parents, and schools.</>,
    },
    {
      num: "05", title: "Account Registration",
      body: "To use parts of the Service, you must create an account by providing accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. You must keep your account information up to date.",
    },
    {
      num: "06", title: "Use of the App and Prohibited Conduct",
      body: "You agree to use Snows ProAm only for lawful, respectful, and legitimate purposes. You must not bully, harass, threaten, abuse, stalk, or intimidate others; post or promote hate speech, discriminatory content, or violence; send spam, scams, or unsolicited messaging; post misleading, false, or deceptive content; impersonate another person or organisation; upload malware or attempt to disrupt the Service; scrape, misuse, or interfere with the platform; or use the Service in a way that harms users, venues, partners, or Snows ProAm.",
    },
    {
      num: "07", title: "Geolocation Services",
      body: "The App uses geolocation services to provide location-based features. By using the App, you consent to the collection and use of your geolocation information as described in our Privacy Policy.",
    },
    {
      num: "08", title: "Venue Bookings",
      body: "Venue bookings made through Snows ProAm are subject to availability and the relevant venue provider's rules, terms, safety requirements, cancellation policy, and operating standards. Snows ProAm acts as an intermediary platform only. The user is responsible for choosing, booking, attending, and using a venue. The venue provider is responsible for the quality, safety, condition, suitability, access, facilities, staff, and operation of the venue, and for anything that occurs at the venue. Snows ProAm is not responsible for venue quality, safety, condition, suitability, or incidents that happen at or in connection with a venue.",
    },
    {
      num: "09", title: "Payments, ProAm Coins, and Subscriptions",
      body: "Some transactions may be completed directly through Stripe, while others may use ProAm Coins. ProAm Coins must be purchased through Stripe and may then be used inside the App for eligible transactions without an additional in-app transaction fee. Stripe fees or other third-party payment fees may apply at the time of purchase. Transaction history, refunds, money collected, withdrawals, and coin balances may be shown in Settings or related account areas. Users may request withdrawal of eligible coin balances to their currency, subject to review, approval, processing checks, and applicable limits. Certain services, including subscriptions, may be purchased directly through Stripe. Subscriptions may renew automatically unless cancelled. Users are responsible for cancelling subscriptions promptly through the available cancellation process if they do not want renewal. Payments and refunds remain subject to applicable laws, payment provider rules, venue or partner terms, and any specific terms shown at checkout.",
    },
    {
      num: "10", title: "User Content",
      body: "You retain ownership of the content you create, upload, submit, or share through Snows ProAm, including profile content, posts, comments, messages, media, activity content, and other user-generated material. By providing User Content, you grant Snows ProAm a worldwide, non-exclusive, royalty-free licence to host, store, process, reproduce, display, distribute, adapt, and use that content as needed to operate, improve, promote, protect, and provide the Service. You confirm that you have the rights needed to share your User Content. We may moderate, remove, restrict, or disable access to User Content that violates these Terms, applicable law, or platform safety standards.",
    },
    {
      num: "11", title: "Health and Fitness Disclaimer",
      body: "Workout content, fitness information, activity recommendations, training plans, performance data, and related materials are provided for general informational purposes only. They are not medical advice and are not a substitute for advice from a qualified healthcare professional. You should consult a healthcare professional before starting any exercise programme, especially if you have a medical condition, injury, or health concern. Exercise carries an inherent risk of injury. You are responsible for exercising safely and within your own ability. Snows ProAm does not guarantee any specific fitness, health, performance, weight, competition, or training result.",
    },
    {
      num: "12", title: "Analytics and Advertising Measurement",
      body: <>Snows ProAm displays Google AdMob banner and interstitial ads and may use Meta App Events for adult advertising measurement. These technologies may process limited app-interaction, consent, device, diagnostic, network, and advertising-identifier data as explained in our <a href="/privacypolicy" style={{ color: C.accent, fontWeight: 600 }}>Privacy Policy</a>. Accounts under 18 receive protected, non-personalised, G-rated Google ad treatment and do not use Meta measurement. Adults can decline personalised advertising or iOS tracking without losing access to the App.</>,
    },
    {
      num: "13", title: "Intellectual Property",
      body: "Snows ProAm owns or licenses the App, website, software, source code, designs, branding, logos, trade names, graphics, platform content, systems, and related intellectual property. Except as allowed by these Terms, you must not copy, modify, reproduce, distribute, sell, lease, reverse engineer, decompile, attempt to extract source code from, misuse, or create derivative works from any part of Snows ProAm or its intellectual property.",
    },
    {
      num: "14", title: "Offline Interactions",
      body: "Snows ProAm may help users discover, communicate, arrange, book, or participate in sports, fitness, social, coaching, or venue-related activities. Any interaction, meeting, session, event, booking attendance, or dispute that happens outside the App is beyond our control and responsibility. Users are responsible for their own decisions, conduct, safety, transport, attendance, and interactions offline.",
    },
    {
      num: "15", title: "Reporting and Investigation",
      body: "Users may report content, accounts, behaviour, bookings, payment issues, safety concerns, or other problems through available in-app tools or by contacting support@snowsproam.com. We will review reports using a fair investigation process, which may include checking available records, contacting relevant users or partners, requesting further information, and assessing the issue against these Terms and applicable policies. We may remove content, restrict features, suspend accounts, issue warnings, refer matters to relevant authorities, or take no action where a report is not supported. We do not guarantee any particular outcome.",
    },
    {
      num: "16", title: "Termination and Suspension",
      body: "We may suspend, restrict, or terminate your account or access to parts of the Service if we believe you have violated these Terms, created risk for other users or partners, misused payments or withdrawals, infringed rights, or acted unlawfully. Where appropriate, we may provide notice or an opportunity to respond, but urgent safety, legal, payment, or platform integrity issues may require immediate action.",
    },
    {
      num: "17", title: "Limitation of Liability",
      body: "To the fullest extent permitted by law, Snows ProAm will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, loss of profits, loss of revenues, loss of data, personal injury arising from user activity, venue issues, offline interactions, third-party services, or events outside our reasonable control. Nothing in these Terms limits liability where it cannot be limited under applicable law.",
    },
    {
      num: "18", title: "Changes to Terms",
      body: "We may update these Terms from time to time to reflect changes to the Service, payments, legal requirements, safety processes, or business operations. When changes are material, we will provide reasonable notice, which may include posting the updated Terms in the App or on the website, updating the effective date, sending an email, or showing an in-app notice. Your continued use of Snows ProAm after the updated Terms take effect means you accept the updated Terms. If you do not agree, you should stop using the Service.",
    },
    {
      num: "19", title: "Governing Law",
      body: "These Terms are governed by the laws of Ireland and the Irish legal framework, including applicable Irish and EU legislation, without regard to conflict of law rules. Any dispute arising from or relating to these Terms or the Service will be subject to the jurisdiction of the Irish courts, unless applicable consumer law gives you mandatory rights to bring a claim elsewhere.",
    },
  ];

  return (
    <div style={{
      background: C.bg, minHeight: "100vh", color: C.text,
      fontFamily: "'DM Sans', sans-serif",
      transition: "background 0.3s ease, color 0.3s ease",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; -webkit-font-smoothing: antialiased; }
        a { color: inherit; text-decoration: none; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${C.scrollbarThumb}; border-radius: 99px; }
        .theme-toggle { transition: transform 0.2s ease; }
        .theme-toggle:hover { transform: rotate(15deg) scale(1.1); }
        .section-card { transition: border-color 0.2s ease, box-shadow 0.2s ease; }
        .section-card:hover { border-color: ${C.accentBorder} !important; box-shadow: ${C.shadowMd} !important; }
        .grad-text {
          background: linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
      `}</style>

      {/* Header */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        height: 64, padding: "0 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: C.headerBg,
        backdropFilter: "blur(20px) saturate(1.2)",
        WebkitBackdropFilter: "blur(20px) saturate(1.2)",
        borderBottom: `1px solid ${C.border}`,
        transition: "background 0.3s ease",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center" }}>
          <img src={logo} alt="Snows ProAm"
            style={{ height: 30, filter: isDark ? "none" : "none" }} />
        </a>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <a href="/" style={{
            fontSize: 13.5, fontWeight: 500, color: C.textSecondary,
            padding: "6px 14px", borderRadius: 8,
            border: `1px solid ${C.border}`,
            background: C.bgElevated,
            transition: "color 0.2s ease",
          }}>← Back</a>

          <button className="theme-toggle" onClick={toggleTheme}
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: C.bgElevated,
              border: `1px solid ${C.border}`,
              color: C.text,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
            }}>
            <FontAwesomeIcon icon={isDark ? faSun : faMoon} style={{ fontSize: 13 }} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <div style={{
        maxWidth: 800, margin: "0 auto",
        padding: "64px 24px 48px",
      }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "6px 14px", borderRadius: 999,
          background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`,
          fontSize: 12, fontWeight: 600, color: C.accent,
          textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 20,
        }}>
          Legal
        </div>

        <h1 style={{
          fontFamily: "'Sora', sans-serif",
          fontSize: "clamp(36px, 6vw, 56px)",
          fontWeight: 800, letterSpacing: -1.2, lineHeight: 1.05,
          marginBottom: 16, color: C.text,
        }}>
          Terms &{" "}
          <span className="grad-text">Service</span>
        </h1>

        <p style={{ fontSize: 16, color: C.textSecondary, lineHeight: 1.6, maxWidth: 520 }}>
          Please read these terms carefully before using Snows ProAm.
          By using our platform you agree to the conditions below.
        </p>

        <div style={{
          marginTop: 24, display: "flex", gap: 16, flexWrap: "wrap",
          fontSize: 13, color: C.textMuted, fontWeight: 500,
        }}>
          <span>Effective: 27 September 2026</span>
          <span style={{ color: C.border }}>·</span>
          <span>Governed by Irish Law</span>
        </div>
      </div>

      {/* Sections */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px 100px" }}>
        <div style={{ display: "grid", gap: 12 }}>
          {sections.map((s) => (
            <div key={s.num} className="section-card" style={{
              background: C.bgCard,
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              padding: "28px 28px",
              position: "relative", overflow: "hidden",
            }}>
              {/* Background number */}
              <div style={{
                position: "absolute", top: 12, right: 20,
                fontSize: 56, fontWeight: 800,
                fontFamily: "'Sora', sans-serif",
                color: isDark ? "rgba(255,255,255,0.03)" : "rgba(12,105,200,0.05)",
                lineHeight: 1, userSelect: "none",
              }}>{s.num}</div>

              <div style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: 11, fontWeight: 700, color: C.accent,
                textTransform: "uppercase", letterSpacing: 0.5,
                marginBottom: 10,
              }}>
                {s.num}
              </div>

              <h3 style={{
                fontFamily: "'Sora', sans-serif",
                fontSize: 17, fontWeight: 700,
                color: C.text, marginBottom: 10, letterSpacing: -0.2,
              }}>
                {s.title}
              </h3>

              <p style={{
                fontSize: 14.5, color: C.textSecondary,
                lineHeight: 1.7, maxWidth: 640,
              }}>
                {s.body}
              </p>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div style={{
          marginTop: 40, padding: "24px 28px",
          background: C.accentBgSubtle,
          border: `1px solid ${C.accentBorder}`,
          borderRadius: 16,
          fontSize: 14, color: C.textSecondary, lineHeight: 1.65,
        }}>
          <span style={{ fontWeight: 700, color: C.accent }}>Questions?</span>{" "}
          If you have any questions about these Terms, please contact us at{" "}
          <a href="mailto:support@snowsproam.com" style={{ color: C.accent, fontWeight: 600 }}>
            support@snowsproam.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default Terms;
